import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { buySelectedLand, selectLand } from "../../../../reducers/hub";
import { listen } from "../../../../reducers/transactions";
import PlotMetadata from "../PlotMetadata";

import usePurchasing from "./hooks/usePurchasing";

import * as Styled from "./styled";
import { isMobile } from "react-device-detect";

const Status = {
  available: { title: "Available", color: "#98faf0", sell: true },
  minted: { title: "Minted", color: "#98A8EF", sell: false },
  yours: { title: "Your land", color: "#7FFA77", sell: false },
  purchasing: { title: "Purchasing...", color: "#98faf0", sell: true },
  undiscovered: { title: "Not Determined", color: "#E6E49F", sell: false },
};

const Zones = [
  { id: "0", icon: require(`./assets/ic-zone-${0}.png`), title: "Zone #1" },
  { id: "1", icon: require(`./assets/ic-zone-${1}.png`), title: "Zone #2" },
  { id: "2", icon: require(`./assets/ic-zone-${2}.png`), title: "Zone #3" },
  { id: "3", icon: require(`./assets/ic-zone-${3}.png`), title: "Zone #4" },
  { id: "4", icon: require(`./assets/ic-zone-${4}.png`), title: "Zone #5" },
  { id: "5", icon: require(`./assets/ic-zone-${5}.png`), title: "Premium" },
];

const UndiscoveredLand = {
  name: "Undiscovered Land",
  owner: "-",
  area: "-",
  place: "-",
  coordinates: "-",
  description: "-",
  address: "-",
};

function Plot(props) {
  const dispatch = useDispatch();

  const walletAddress = useSelector((state) => state.wallet.account.address);
  const ownerAddress = useSelector((state) => state.hub.selected?.owner);

  const landId = useSelector((state) => state.hub.selected?.id);

  const zone = useSelector((state) => state.hub.selected?.zone);
  const geo = useSelector((state) => state.hub.selected?.geo);
  const meta = useSelector((state) => state.hub.selected?.meta);
  const area = useSelector((state) => state.hub.selected?.area);

  const [land, updateLand] = useState(null);

  useEffect(() => {
    updateLand(null);
  }, [landId, updateLand]);

  useEffect(() => {
    if (zone && !land?.zone && !zone?.error) {
      updateLand({ ...land, zone: Zones[zone] });
    }

    if (area && !land?.area) {
      updateLand({ ...land, area: area });
    }

    if (geo && !land?.geo) {
      const coordinates = geo.coordinates;
      switch (coordinates) {
        case undefined: {
          updateLand({ ...land, geo: { name: null, place: null, coordinates: null } });
          break;
        }
        default: {
          const coordinates = geo.coordinates[0] + ", " + geo.coordinates[1];
          updateLand({ ...land, geo: { name: geo.name, place: geo.place, coordinates: coordinates } });
          break;
        }
      }
    }

    if (meta && !land?.meta) {
      updateLand({ ...land, meta: meta });
    }
  }, [land, zone, geo, meta, area, updateLand]);

  const mintingPrice = useSelector((state) => state.hub.selected?.price);
  const mintingGasPrice = useSelector((state) => state.hub.selected?.gas);

  const [isPurchasing, isPurchaseHappened] = usePurchasing(landId);

  const [displayPlotMetadata, setDisplayPlotMetadata] = useState(false);

  const isSelling = useSelector((state) => !state.hub.selected?.sold);
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (isPurchasing) {
      setStatus(Status.purchasing);
      return;
    }

    const isOwner = walletAddress?.toLowerCase() === ownerAddress?.toLowerCase();

    setStatus(isSelling ? Status.available : isOwner ? Status.yours : Status.minted);
  }, [isSelling, isPurchasing, walletAddress, ownerAddress, geo]);

  useEffect(() => {
    if (isPurchaseHappened && landId) {
      dispatch(selectLand(landId));
    }
  }, [dispatch, isPurchaseHappened, landId]);

  const txHash = useSelector((state) => state.hub.selected?.txHash);
  useEffect(() => {
    if (txHash == null || landId == null) return;

    dispatch(listen(txHash, { landId: landId }));
  }, [txHash, landId, dispatch]);

  const handleMint = () => {
    if (!walletAddress) {
      props.onOpenConnectWallet(true);
      return;
    }

    if (!isPurchasing) {
      dispatch(buySelectedLand());
    }
  };

  return (
    <>
      {displayPlotMetadata && <PlotMetadata metadata={land?.meta} onClose={() => setDisplayPlotMetadata(false)} />}
      {(!isMobile || !displayPlotMetadata) && (
        <Styled.PlotContainer>
          {land == null || (land.meta === undefined && land.geo === undefined) ? (
            <>
              <Styled.TitleSkeleton />
              <Styled.TitleSkeleton />
              <Styled.StatusSkeleton />
              <Styled.Spacer />
              <Styled.ZoneSkeleton />
              <Styled.Spacer />
              <Styled.Info caption="Area" />
              <Styled.InfoSkeletonLarge />
              <Styled.Info caption="Place" />
              <Styled.InfoSkeletonSmall />
              <Styled.Info caption="Coordinates" />
              <Styled.InfoSkeletonLarge />
              <Styled.Info caption="Owner" />
              <Styled.InfoSkeletonSmall />
              <Styled.Info caption="Address" />
              <Styled.InfoSkeletonLarge />
              <Styled.Info caption="Description" value={{ size: "14px", weight: "400" }} />
              <Styled.InfoSkeletonSmall />
            </>
          ) : (
            <>
              <Styled.Title>{land?.meta?.name ?? land?.geo?.name ?? UndiscoveredLand.name}</Styled.Title>
              <Styled.Status
                status={land?.meta?.name ? status : land?.geo?.name ? status : Status.undiscovered}
                onEdit={() => setDisplayPlotMetadata(true)}
                editable={status === Status.yours}
              />
              <Styled.Spacer />
              <Styled.Zone icon={land?.zone?.icon ?? Zones[2].icon}>{land?.zone?.title ?? Zones[2].title}</Styled.Zone>
              <Styled.Spacer />
              <Styled.Info caption="Area">{land?.area ?? UndiscoveredLand.area} ㎡</Styled.Info>
              <Styled.Info caption="Place">{land?.geo?.place ?? UndiscoveredLand.place}</Styled.Info>
              <Styled.Info caption="Coordinates">{land?.geo?.coordinates ?? UndiscoveredLand.coordinates}</Styled.Info>
              <Styled.Info caption="Owner">{land?.meta?.ownerName ?? UndiscoveredLand.ownerName}</Styled.Info>
              <Styled.Info caption="Address">{land?.geo?.name ?? UndiscoveredLand.address}</Styled.Info>
              <Styled.Spacer />
              <Styled.Info caption="Description" value={{ size: "14px", weight: "400" }}>
                {land?.meta?.description}
              </Styled.Info>
            </>
          )}

          {status?.sell && mintingPrice && (
            <>
              <Styled.Spacer />

              <Styled.Price caption="Minting price">{mintingPrice} ETH</Styled.Price>
              {mintingGasPrice && <Styled.Fees caption="Est. Fee">{mintingGasPrice} ETH</Styled.Fees>}
              <Styled.Mint disabled={isPurchasing} onClick={() => handleMint()}>
                Mint
              </Styled.Mint>
            </>
          )}
        </Styled.PlotContainer>
      )}
    </>
  );
}

export default Plot;
